import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { cloneDeep } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { EncounterMedicalCode } from "@/pages/app/provider/billings/billing-types";
import Flex from "../../../../components/Flex";
import { useRoleController } from "../../../../components/useRoleController";
// import { PROVIDER_PATHS } from "../../../../constants/paths";
import {
  ADD_SUPER_BILL_INITIAL_STATE,
  ADD_SUPER_BILL_PROCEDURE_INITIAL_STATE,
  patientBalance,
} from "../../../../constants/provider/super-bill/add-super-bill/add-super-bill";
import { useLoadingContext } from "../../../../context/LoadingProvider";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useAddSuperBill } from "../../../../queries/billings/use-add-super-bill";
import { useGetCodesForSuperBills } from "../../../../queries/billings/use-get-code-for-super-bill";
import { useUpdateSuperBill } from "../../../../queries/billings/use-update-super-bill";
import {
  DEFAULT_VALUE_LABEL_LIST,
  getErrorSnackbar,
  getIdFromObj,
  getValueFromObj,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
  transformMultiSelectBackEndVal,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import { Elements } from "@stripe/react-stripe-js";
import PaymentDrawer from "../../../../components/app/provider/billing/payment/PaymentDrawer";
import { getStripePromise } from "../../../../queries/stripe/use-stripe";
import DiagnosisCodeGrid from "./add-super-bill-components/DiagnosisCodeGrid";
import ProcedureCodeGrid from "./add-super-bill-components/ProcedureCodeGrid";
import ProviderDetails from "./add-super-bill-components/ProviderDetails";
import ServiceDetailsProvider from "./add-super-bill-components/ServiceDetailsProvider";
import SuperBillCalculation from "./add-super-bill-components/SuperBillCalculation";
import {
  procedureCodeValidationSchema,
  superBillValidationSchema,
} from "./add-super-bill-const";
import { convertDiagnosisCodesData } from "./claims/claims-constants";
import { useFormScrollIntoView } from "../../../../hooks/use-form-scrollInto-view";
import { useRenderingProviderInfo } from "./use-rendering-provider-info";
import {
  ENCOUNTER_PREFILL_ROUTE_SEGMENTS,
  INVOICE_ROUTE_SEGMENT,
  INVOICE_ROUTE_SEGMENT_INDEX,
} from "./billing-constant";
import { useGetSuperBill } from "@/queries/billings/use-get-super-bill";
import { PatientProcedureCode } from "@/services/api";
import { AsyncSelectValue } from "@/components/AsyncSelect";

interface AddSuperBillProps {
  patientValidationFormik?: any;
  patientsData?: any;
  patientDataBillDetails?: any;
  viewBill?: boolean;
  superBillId?: string;
  totalAmount?: number;
  setTotal?: (total: number) => void;
  isPatientResponsibilityInvoice?: boolean;
  // ECRH-5347: invoice type selected from the "Create Invoice" dropdown; posted
  // with the SuperBill (SELF_PAY / OUT_OF_NETWORK / PR).
  invoiceType?: string;
}

const AddSuperBill = ({
  patientValidationFormik,
  patientsData,
  patientDataBillDetails,
  viewBill = true,
  superBillId,
  totalAmount,
  setTotal,
  isPatientResponsibilityInvoice = false,
  invoiceType,
}: AddSuperBillProps) => {
  const formik = useFormik({
    initialValues: ADD_SUPER_BILL_INITIAL_STATE,
    validationSchema: superBillValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  useFormScrollIntoView(formik);

  const [diagnosisCodes, setDiagnosisCodes] = useState<any>(undefined);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const paymentMode =
    searchParams.get("paymentMode") ??
    (patientDataBillDetails as any)?.paymentMode;
  const isSelfPay = paymentMode === "Self Pay";
  const queryClient = useQueryClient();
  const isUpdateRoute = location.pathname;
  // Tracks which button triggered the save so the success handler knows whether
  // to show the confirmation modal or open the payment drawer directly.
  const collectDirectlyRef = useRef(false);
  // ECRH-6052: uuid of the invoice created from this screen. "Save & Collect"
  // saves the invoice before opening the payment drawer, so cancelling the
  // payment leaves the user on the create screen with an invoice that already
  // exists. Any further save has to update that invoice (PUT) instead of
  // posting a duplicate, which the backend rejects with "Invoice already
  // created".
  const [createdSuperBillUuid, setCreatedSuperBillUuid] = useState<
    string | undefined
  >(undefined);
  const isUpdateInvoiceRoute =
    location.pathname.split("/")[INVOICE_ROUTE_SEGMENT_INDEX] ===
    INVOICE_ROUTE_SEGMENT.UPDATE;
  const procedureCodeFormik = useFormik<
    typeof ADD_SUPER_BILL_PROCEDURE_INITIAL_STATE
  >({
    initialValues: [],
    validationSchema: procedureCodeValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });
  const setProcedureRowsFromInvoice = (rows: PatientProcedureCode[]) => {
    procedureCodeFormik.setValues(
      rows.map((item) => ({
        ...item,
        // DEFAULT_VALUE_LABEL_LIST makes the transform emit { value, label } rows.
        modifiers: transformMultiSelectFrontEndVal(
          DEFAULT_VALUE_LABEL_LIST,
          item?.modifiers
        ) as AsyncSelectValue[] | undefined,
      }))
    );
  };
  const setFieldFormik = (
    field: string,
    value: any,
    transform: (v: any) => any = (v) => v
  ) => {
    formik.setFieldValue(
      field,
      value === "null" ? undefined : transform(value)
    );
  };

  const [calculationData, setCalculationData] = useState<patientBalance>({
    subTotal: undefined,
    previousBalance: undefined,
    advancedPaid: undefined,
    insuranceAmount: undefined,
    total: undefined,
  });
  // Calculate Subtotal (sum of all charges)
  const total = procedureCodeFormik.values.reduce(
    (acc, curr) => acc + (parseFloat(curr.total as any) || 0),
    0
  );

  useEffect(() => {
    if (setTotal) {
      // previously setting allprocedure code sum to payment
      // setTotal(parseFloat(total.toFixed(2)));

      // setting subtotal to calculation data
      setCalculationData((prev) => ({
        ...prev,
        subTotal: total.toFixed(2),
      }));
    }
  }, [total, setTotal]);

  // NOTE: forwarding the amount to collect to the parent is handled by
  // <SuperBillCalculation/>'s `setTotal`, which sends the full Total Balance
  // (sub-total - adv paid - insurance + prev. balance). The earlier effect
  // here forwarded `calculationData.total || subTotal`, which fell back to the
  // sub-total whenever the user didn't edit a calculation field — causing the
  // "Collect Payment" flow to charge the sub-total instead of the Total Balance.

  useEffect(() => {}, [formik.values, formik.errors]);

  // this code is to populate data from super bill ( both view and edit )
  useEffect(() => {
    // console.log("useEffect run", patientDataBillDetails);

    // to-do - we need to change this formik setter to one common object.

    // The Create Invoice (add-invoice) flow forwards a balance-only object so
    // the Patient Balance section auto-fills, while service details / codes come
    // from the selected encounter. Only populate service fields when the source
    // actually carries them, so we don't blank the encounter-driven values.
    const hasServiceDetails = !!(
      patientDataBillDetails?.claimUuid ||
      patientDataBillDetails?.location ||
      patientDataBillDetails?.patientProcedureCodes?.length ||
      patientDataBillDetails?.diagnosisCodes
    );

    if (hasServiceDetails) {
      formik.setFieldValue(
        "uuid",
        patientDataBillDetails?.patient ?? undefined
      );
      formik.setFieldValue(
        "locationId",
        patientDataBillDetails?.location ?? undefined
      );
      formik.setFieldValue(
        "placeOfService",
        patientDataBillDetails?.placeOfService ?? undefined
      );
      formik.setFieldValue(
        "serviceState",
        patientDataBillDetails?.serviceState ?? undefined
      );
      formik.setFieldValue(
        "dateOfService",
        patientDataBillDetails?.dateOfService
          ? new Date(patientDataBillDetails?.dateOfService)
          : undefined
      );
      formik.setFieldValue(
        "renderingproviderId",
        patientDataBillDetails?.renderingProvider ?? undefined
      );
      formik.setFieldValue(
        "priorAuthorization",
        patientDataBillDetails?.priorAuthorization ?? undefined
      );

      formik.setFieldValue(
        "billingProviderId",
        patientDataBillDetails?.billingProvider ?? undefined
      );
      formik.setFieldValue(
        "billingProvideNPINumber",
        patientDataBillDetails?.billingProviderNpi ?? undefined
      );
      formik.setFieldValue(
        "referringProviderId",
        patientDataBillDetails?.referringProvider ?? undefined
      );
      formik.setFieldValue(
        "referringProviderNPINumber",
        patientDataBillDetails?.referringProviderNpi ?? undefined
      );
      formik.setFieldValue(
        "orderingProviderId",
        patientDataBillDetails?.orderingProvider ?? undefined
      );
      formik.setFieldValue(
        "orderingProviderNPINumber",
        patientDataBillDetails?.orderingProviderNpi ?? undefined
      );
      formik.setFieldValue(
        "ecarehealthPatientId",
        patientDataBillDetails?.ecarehealthPatientId ?? undefined
      );
      formik.setFieldValue(
        "subTotal",
        patientDataBillDetails?.subTotal ?? undefined
      );
      formik.setFieldValue(
        "insuranceAmount",
        patientDataBillDetails?.insuranceAmount ?? undefined
      );
      formik.setFieldValue("total", patientDataBillDetails?.total ?? undefined);
    }

    formik.setFieldValue(
      "advancedPaid",
      patientDataBillDetails?.advancedPaid ?? undefined
    );
    formik.setFieldValue(
      "previousBalance",
      patientDataBillDetails?.previousBalance ?? undefined
    );

    setCalculationData((prev) => ({
      ...prev,
      previousBalance: patientDataBillDetails?.previousBalance ?? undefined,
      advancedPaid: patientDataBillDetails?.advancedPaid ?? undefined,
      ...(hasServiceDetails
        ? {
            subTotal: patientDataBillDetails?.subTotal ?? undefined,
            insuranceAmount:
              patientDataBillDetails?.insuranceAmount ?? undefined,
            total: patientDataBillDetails?.total ?? undefined,
          }
        : {}),
    }));

    if (patientDataBillDetails?.patientProcedureCodes?.length) {
      setProcedureRowsFromInvoice(
        patientDataBillDetails?.patientProcedureCodes
      );
    }
    if (patientDataBillDetails?.diagnosisCodes) {
      setDiagnosisCodes(
        convertDiagnosisCodesData(patientDataBillDetails?.diagnosisCodes)
      );
    }
  }, [patientDataBillDetails]);

  // NPI / License TX from the provider record (the saved superBill returns the
  // license as null), same source the side section and the listing print use.
  const renderingProviderInfo = useRenderingProviderInfo(
    patientDataBillDetails
  );

  // "Print Report" renders the same <SuperBillReport/> as the invoice listing's
  // print action. The form only holds the fields it edits, so the patient
  // details, the rendering provider's license, the paid amount and the patient
  // balance are merged in from the saved SuperBill — otherwise those blocks
  // print blank.
  const reportFormData = useMemo(
    () => ({
      ...formik.values,
      uuid: patientDataBillDetails?.patient ?? formik.values.uuid,
      ecarehealthPatientId:
        patientDataBillDetails?.ecarehealthPatientId ?? undefined,
      patientDOB: patientDataBillDetails?.patientDOB ?? undefined,
      patientPhone: patientDataBillDetails?.patientPhone ?? undefined,
      patientAddress: patientDataBillDetails?.patientAddress ?? undefined,
      dateGenerated: patientDataBillDetails?.billDate ?? undefined,
      renderingProviderNpi:
        renderingProviderInfo?.npi ??
        patientDataBillDetails?.renderingProviderNpi ??
        undefined,
      renderingProviderLicenseNumber:
        renderingProviderInfo?.licenseNumber ??
        patientDataBillDetails?.renderingProviderLicenseNumber ??
        undefined,
      prevPaid: patientDataBillDetails?.prevPaid?.toString() ?? undefined,
      patientBalance:
        patientDataBillDetails?.patientBalance?.toString() ?? undefined,
    }),
    [formik.values, patientDataBillDetails, renderingProviderInfo]
  );

  // Switching the visit (or, on a PR invoice, the claim) starts a different
  // invoice, so the one saved earlier on this screen must not be updated with
  // it.
  const selectedClaimUuid = patientValidationFormik?.values?.claim?.value;
  useEffect(() => {
    setCreatedSuperBillUuid(undefined);
  }, [superBillId, selectedClaimUuid]);

  // Later saves update the invoice by its saved CPT row uuids, so the rows are
  // reloaded from it once the payment drawer closes.
  const { refetch: refetchCreatedSuperBill } = useGetSuperBill({
    superBillUuid: createdSuperBillUuid ?? "",
    xTENANTID: getXtenantId(),
  });
  const reloadCreatedSuperBillRows = () => {
    if (!createdSuperBillUuid) return;
    refetchCreatedSuperBill().then(({ data }) => {
      const savedRows = Array.from(data?.data?.patientProcedureCodes ?? []);
      if (savedRows.length) setProcedureRowsFromInvoice(savedRows);
    });
  };
  // Save & Collect Payment (ECRH-5371): opens the payment drawer directly.
  const directPayDisc = useDisclosure(false, {
    onClose: reloadCreatedSuperBillRows,
  });

  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  const { mutateAsync: createSuperBill } = useAddSuperBill();
  const { mutateAsync: updateSuperBill } = useUpdateSuperBill();

  const {
    data: allsuperBillCodes,
    isFetching,
    isLoading,
  } = useGetCodesForSuperBills({
    appointmentUuid: superBillId ?? "",
    cptBilled: false,
    xTENANTID: getXtenantId(),
  });

  // this code is to populate data from encounter
  // "invoice" -> invoice-from-encounter (URL driven),
  // "add-invoice" -> new invoice where the Select Visit dropdown supplies the
  // encounter id (ECRH-5087). Both auto-fill service details from the encounter.
  useEffect(() => {
    // A Patient Responsibility invoice is pre-filled from the claim it settles
    // (service details + the CPT rows carrying the ERA copay / coinsurance /
    // deductible breakdown), so the encounter must not overwrite any of it.
    if (isPatientResponsibilityInvoice) return;
    if (
      ENCOUNTER_PREFILL_ROUTE_SEGMENTS.includes(
        isUpdateRoute.split("/")[INVOICE_ROUTE_SEGMENT_INDEX]
      )
    ) {
      setDiagnosisCodes(
        (allsuperBillCodes?.data?.diagnosisMedicalCodeSet?.length ?? 0) > 0
          ? allsuperBillCodes?.data?.diagnosisMedicalCodeSet
          : undefined
      );
      // code for procedure code

      const transformProcedureList = (list?: EncounterMedicalCode[]) => {
        return list?.map((item) => ({
          uuid: item.uuid,
          procedureCode: {
            [item.uuid]: `${item.code} - ${item.description}`,
          },
          // carry the code classification (CPT/HCPCS) so the badge reflects it
          type: item.type,
          // default the unit/count to 1 for codes prefilled from the encounter
          quantity: 1,
          active: true,
          archive: false,
        }));
      };
      const op = transformProcedureList(
        (allsuperBillCodes?.data?.procedureMedicalCodeSet?.length ?? 0) > 0
          ? allsuperBillCodes?.data?.procedureMedicalCodeSet
          : undefined
      );
      if (op) {
        procedureCodeFormik.setValues(op);
      }

      // this section will auto populate the service details section
      setFieldFormik(
        "locationId",
        allsuperBillCodes?.data?.location ?? undefined
      );
      setFieldFormik(
        "serviceState",
        allsuperBillCodes?.data?.serviceState ?? undefined
      );
      setFieldFormik(
        "dateOfService",
        allsuperBillCodes?.data?.dateOfService ?? undefined
      );
      setFieldFormik(
        "renderingproviderId",
        allsuperBillCodes?.data?.renderingProvider ?? undefined
      );

      // condition for place of service  mode - "IN_PERSON" | "VIRTUAL"
      // condition for place of service  "IN_PERSON" | "VIRTUAL"
      // mode

      if (allsuperBillCodes?.data?.mode === "VIRTUAL") {
        setFieldFormik("placeOfService", "TELEHEALTH_INSIDE_PATIENT_HOME");
      } else if (allsuperBillCodes?.data?.mode === "IN_PERSON") {
        setFieldFormik("placeOfService", "OFFICE");
      } else {
        setFieldFormik("placeOfService", undefined);
      }
    }
  }, [allsuperBillCodes]);

  async function onSubmit(values: any) {
    if (!patientValidationFormik?.isValid) return;

    // Block save when any CPT row has an invalid discount (e.g. discount amount
    // greater than the charge amount, or percentage over 100). (ECRH-5380 - Issue 7)
    const procedureErrors = await procedureCodeFormik.validateForm();
    if (
      Array.isArray(procedureErrors)
        ? procedureErrors.some(Boolean)
        : Object.keys(procedureErrors ?? {}).length > 0
    ) {
      showSnackbar({
        message: "Please correct the highlighted discount before saving.",
        type: "error",
      });
      return;
    }

    // A $0.00 Total Balance is valid when a real charge has been fully
    // discounted (100% / discount equal to the charge). Only block when the
    // invoice has no billable charge at all.
    // const hasBillableCharge = procedureCodeFormik.values?.some(
    //   (row) => Number(row?.charges) > 0
    // );
    // if (!hasBillableCharge) {
    //   showSnackbar({
    //     message:
    //       "Invoice cannot be created with zero amount. Please add billable items.",
    //     type: "error",
    //   });
    //   return;
    // }

    // double check for validationSchema.
    // Validation for procedure codes
    // ? this validation removed suggested  by @bhushan-varade it will provide from the B.E
    // if (
    //   !procedureCodeFormik.values ||
    //   procedureCodeFormik.values.length === 0
    // ) {
    //   showSnackbar({
    //     message: "At least one procedure code is required",
    //     type: "error",
    //   });
    //   return;
    // }

    // // Validation for diagnosis codes
    // if (!values.diagnosisCodes || values.diagnosisCodes.length === 0) {
    //   console.log("step 3");
    //   showSnackbar({
    //     message: "At least one diagnosis code is required",
    //     type: "error",
    //   });
    //   return;
    // }

    const copiedValues: any = cloneDeep(values);
    // console.log("Patient details ", copiedValues);
    // console.log("procedureCodeFormik ", procedureCodeFormik?.values);
    const patientProcedurePostParams = procedureCodeFormik?.values?.map(
      (itm) => ({
        ...itm,
        modifiers:
          Array.isArray(itm?.modifiers) && itm.modifiers.length > 0
            ? transformMultiSelectBackEndVal(
                itm.modifiers.filter(
                  (mod) => mod && Object.keys(mod).length > 0
                )
              )
            : null,
      })
    );

    // Visit (encounter) id: from the encounter-driven flow (superBillId) or,
    // for a PR invoice created from a claim, the id carried on the claim's
    // encounter object. Send only the id as the key with an empty value.
    const visitId =
      superBillId || getIdFromObj(patientDataBillDetails?.encounter);

    const params = getUnNecessaryTransformData(["diagnosisCodes"], {
      uuid: patientDataBillDetails?.uuid ?? createdSuperBillUuid ?? undefined,
      patient: {
        [patientsData?.uuid]: `${patientsData?.firstName} ${patientsData?.lastName}`,
      },
      dateOfService: copiedValues?.dateOfService
        ? new Date(copiedValues?.dateOfService).toISOString()
        : undefined,
      placeOfService: copiedValues?.placeOfService ?? undefined,
      location: copiedValues?.locationId ?? undefined,
      serviceState: copiedValues?.serviceState ?? undefined,
      // ECRH-5370: resolved Billing Address (selected Service Location, or the
      // Provider Group Profile when none) for the Claim / CMS-1500 Box 32.
      serviceLocationAddress: copiedValues?.serviceLocationAddress ?? null,
      renderingProvider: copiedValues?.renderingproviderId ?? undefined,
      priorAuthorization: copiedValues?.priorAuthorization ?? null,
      billingProvider: copiedValues?.billingProviderId ?? null,
      // Billing Provider = Practice Group (Billing Entity). Send the practice
      // name (tenant identifier) so the backend resolves the practice/group
      // details, mirroring the Claim flow (ECRH-5370 / ECRH-5422).
      practiceName: getXtenantId(),
      referringProvider: copiedValues?.referringProviderId ?? null,
      orderingProvider: copiedValues?.orderingProviderId ?? null,
      ecarehealthPatientId: copiedValues?.ecarehealthPatientId ?? null,
      // procedureCodes: copiedValues?.diagnisisCodeData ?? null,

      diagnosisCodes: copiedValues?.diagnisisCodeData ?? null,

      encounter: visitId ? { [visitId]: "" } : undefined,

      patientProcedureCodes: patientProcedurePostParams,
      // this is the total of the procedure charges
      subTotal: total,

      // note:  i need to post this parameters in the super bill API after calculation flow done.
      total: calculationData.total ?? undefined,
      advancedPaid: calculationData.advancedPaid ?? undefined,
      insuranceAmount: calculationData.insuranceAmount ?? undefined,
      previousBalance: calculationData.previousBalance ?? undefined,

      superBillStatus: "PENDING",
      patientBalance: Number(calculationData.total) ?? undefined,
      patientResponsibilityInvoice: isPatientResponsibilityInvoice,
      // ECRH-5347: invoice type chosen from the Create Invoice dropdown.
      invoiceType: invoiceType ?? undefined,
      paymentMode: paymentMode ?? undefined,
      // Claim linked to the invoice: the one picked in the header's Select
      // Claim dropdown (Patient Responsibility flow), falling back to the claim
      // the invoice was launched from when there is no dropdown on the screen.
      claimUuid:
        patientValidationFormik?.values?.claim?.value ??
        patientDataBillDetails?.claimUuid ??
        undefined,
    });
    // console.log("POST PARAMS ", patientProcedurePostParams);
    // return params;

    const transFormApiPayload = {
      superBill: params,
      xTENANTID: getXtenantId(),
    };
    // ECRH-6052: the edit screen always updates; the create screen updates too
    // once it has already saved the invoice (Save & Collect -> Cancel), so the
    // user's further changes land on that same invoice instead of a rejected
    // duplicate.
    const isExistingInvoice = isUpdateInvoiceRoute || !!createdSuperBillUuid;
    showToastAndReset(
      isExistingInvoice
        ? updateSuperBill(transFormApiPayload as any)
        : createSuperBill(transFormApiPayload as any),
      isExistingInvoice
    );
  }
  const closeAndReset = () => {
    formik.resetForm();
  };

  const showToastAndReset = (
    apiCall: Promise<any>,
    isExistingInvoice: boolean = false
  ) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        // A create returns the new invoice id; an update only confirms, so the
        // id already held on this screen stays the reference.
        const savedSuperBillId = isExistingInvoice
          ? (createdSuperBillUuid ?? res?.data?.data ?? undefined)
          : (res?.data?.data ?? undefined);

        // Keep the id so the next save on this screen updates this invoice.
        if (!isExistingInvoice && savedSuperBillId) {
          setCreatedSuperBillUuid(savedSuperBillId);
        }

        if (collectDirectlyRef.current) {
          // Save & Collect Payment: open the payment drawer straight away.
          directPayDisc.open({
            uuid: null,
            patientId: patientsData?.uuid,
            superBillId: savedSuperBillId || superBillId,
            subTotal: calculationData.subTotal,
          });
        } else {
          // Save Bill: just persist the invoice and navigate away.
          saveAndNavigate();
        }
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const { setIsLoading } = useLoadingContext();

  useEffect(() => {
    setIsLoading(isFetching || isLoading);
  }, [isFetching, isLoading]);

  function saveAndNavigate() {
    // localStorage.setItem("billingSelectedTab", "super_bill");
    queryClient.invalidateQueries({ queryKey: ["superbill-List-V2"] });
    queryClient.invalidateQueries({ queryKey: [""] });
    queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
    navigate(-1);
    setTimeout(closeAndReset, 0);
  }

  const { hasUpdate: hasUpdateSuperbill } =
    useRoleController("Generate Superbill");

  return (
    <Flex width={"100%"} overflow={"scroll"} flexDirection={"column"}>
      <Flex flexDirection={"column"} gap={1.2} mt={1}>
        {/* Save & Collect Payment (ECRH-5371): opens the payment drawer
            directly, bypassing the confirmation modal. */}
        <Elements stripe={getStripePromise()}>
          <PaymentDrawer
            disclosureHost={directPayDisc}
            amount={(totalAmount ?? 0)?.toString()}
            onSuccess={saveAndNavigate}
            shouldPassPatientId={true}
            providerId={
              getIdFromObj(formik.values.renderingproviderId)
                ? {
                    uuid: getIdFromObj(formik.values.renderingproviderId) ?? "",
                    name:
                      getValueFromObj(formik.values.renderingproviderId) ?? "",
                  }
                : undefined
            }
          />
        </Elements>

        <ServiceDetailsProvider
          formik={formik}
          viewBill={viewBill}
          procedureCodeFormik={procedureCodeFormik as any}
          reportData={reportFormData}
          isPatientResponsibilityInvoice={isPatientResponsibilityInvoice}
        />

        <ProviderDetails
          formik={formik}
          viewBill={viewBill}
          isPatientResponsibilityInvoice={isPatientResponsibilityInvoice}
        />
        <DiagnosisCodeGrid
          selectedPointers={procedureCodeFormik.values}
          formik={formik}
          viewBill={viewBill}
          diagnosisCodes={diagnosisCodes}
          isFetching={false}
          isLoading={false}
          isPatientResponsibilityInvoice={isPatientResponsibilityInvoice}
        />
        <ProcedureCodeGrid
          diagnosisCodesLength={formik?.values?.diagnisisCodeData?.length ?? 0}
          procedureCodeFormik={procedureCodeFormik as any}
          viewBill={viewBill}
          isFetching={false}
          isLoading={false}
          renderingProviderUuid={getIdFromObj(
            formik.values.renderingproviderId
          )}
          isPatientResponsibilityInvoice={isPatientResponsibilityInvoice}
        />
        <SuperBillCalculation
          total={total}
          calculationData={calculationData}
          setCalculationData={setCalculationData}
          viewBill={viewBill}
          isSelfPay={isSelfPay}
          isPatientResponsibilityInvoice={isPatientResponsibilityInvoice}
          setTotal={(total: string) =>
            setTotal?.(total ? parseFloat(total?.toString()) : 0)
          }
        />
      </Flex>
      {viewBill && hasUpdateSuperbill && (
        <Box sx={{ marginLeft: "auto", padding: "20px" }}>
          <Flex gap={1.5}>
            <Button
              // removing disabled condition as it is not required
              onClick={() => {
                collectDirectlyRef.current = false;
                patientValidationFormik?.handleSubmit();
                formik.submitForm();
              }}
              variant="contained"
            >
              Save Bill
            </Button>
            {/* Nothing to collect on a fully-discounted ($0.00) invoice, so
                hide the collect action. (ECRH-5380 - Issue 8) */}
            {/* Gated on `total` (the procedure Subtotal), which stays positive
                even after Adv Paid / Insurance clear the balance:
            {Number(total) > 0 && ( ... )} */}
            {/* Collecting only makes sense while the patient still owes
                something, so gate on the Total Balance - the last Patient
                Balance field, and the amount the payment drawer actually
                charges. Applies to every invoice type (PR, Self-Pay, OON);
                Save Bill stays enabled so a $0.00 invoice can still be saved
                and shown as Paid. */}
            <Button
              disabled={!(Number(totalAmount) > 0)}
              onClick={() => {
                collectDirectlyRef.current = true;
                patientValidationFormik?.handleSubmit();
                formik.submitForm();
              }}
              variant="contained"
            >
              Save &amp; Collect Payment
            </Button>
          </Flex>
        </Box>
      )}
    </Flex>
  );
};

export default AddSuperBill;
